import cx from 'classix';
import { forwardRef } from 'react';
import { Icon, IconProps } from '../atoms';
import defaultAvatarImage from './avatar-default.png';

export type AvatarProps = (
  | {
      type: 'icon';
      icon: IconProps['type'];
      renderAsDiv?: boolean; //Used when the avatar is a child of a button
    }
  | {
      type: 'initials';
      name: string;
      renderAsDiv?: boolean; //Used when the avatar is a child of a button
    }
  | {
      type: 'avatar';
      avatarUrl?: string;
      renderAsDiv?: boolean; //Used when the avatar is a child of a button
    }
) & {
  size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '5xl' | '9xl' | 'custom';
  // TODO: Remove/rework
  customSize?: number;
  customIconSize?: number;
  additionalClassNames?: string;
  onClick?: () => void;
};

const getInitials = (fullName: string) => {
  const names = fullName.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();

  //Strip out middle initials if present
  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }

  return initials;
};

const sizeToIconPx = {
  xs: '16',
  sm: '20',
  md: '24',
  lg: '28',
  xl: '32',
  '2xl': '32',
  '5xl': '32',
  // TODO: I had to add an "enourmous" size here for the directory pages; avoiding here in favor of finding a better solution
  '9xl': '32',
  custom: '', // Custom size is specified manually
};

const ButtonTag = forwardRef<
  HTMLButtonElement,
  React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>
>((props, ref) => {
  return <button {...props} ref={ref} />;
});

const DivTag = forwardRef<
  HTMLDivElement,
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
>((props, ref) => {
  return <div {...props} ref={ref} />;
});

export const Avatar = (props: AvatarProps) => {
  const Element = props.renderAsDiv ? DivTag : ButtonTag;

  return (
    <Element
      onClick={props.onClick}
      className={cx(
        'bg-white',
        props.size === 'xs' && 'w-6 h-6 text-xs',
        props.size === 'sm' && 'w-8 h-8 text-sm',
        props.size === 'md' && 'w-10 h-10 text-md',
        props.size === 'lg' && 'w-12 h-12 text-lg',
        props.size === 'xl' && 'w-14 h-14 text-xl',
        props.size === '2xl' && 'w-16 h-16 text-2xl',
        props.size === '5xl' && 'w-[113px] h-[113px] text-2xl',
        props.size === '9xl' && 'w-[198px] h-[198px] text-2xl',
        'rounded-full',
        (props.type === 'icon' || props.type === 'initials') && '!bg-avatar-bg',
        'flex',
        'justify-center',
        'items-center',
        'border-[0.75px] border-black/8',
        'font-semibold',
        'text-avatar-text',
        'bg-cover',
        props.onClick && 'focus:ring-4 ring-avatar-focus-ring ring-opacity-[14]',
        !props.onClick && 'cursor-default',
        props.additionalClassNames,
      )}
      //Tailwind doesn't handle non-local url background images well, so we use a style here
      style={{
        backgroundImage:
          props.type === 'avatar' ?
            props.avatarUrl ?
              `url(${props.avatarUrl})`
            : `url(${defaultAvatarImage})`
          : undefined,
        // TODO: Revisit avatar sizing in this projet
        width: props.size === 'custom' && props.customSize ? `${props.customSize}px` : undefined,
        height: props.size === 'custom' && props.customSize ? `${props.customSize}px` : undefined,
      }}
    >
      {props.type === 'icon' && (
        <Icon
          type={props.icon}
          size={
            props.size === 'custom' ? props.customIconSize?.toString() : sizeToIconPx[props.size]
          }
        />
      )}
      {props.type === 'initials' && getInitials(props.name)}
    </Element>
  );
};

/** The two usage styles of the <Avatar> component: you must either provide an icon to show the avatar url is missing or broken,
 * or a string name that will be converted to a two-character intials string. */
export type AvatarIconOrName =
  | {
      /* Icon used as a fallback if the url is null */
      fallbackIcon: IconProps['type'];
    }
  | {
      /* Name used to generate initials as a fallback if the url is null */
      name: string;
    };

export type AvatarNewProps = {
  url?: string | null;
  alt?: string;
  /* Allows declaring that the image should have a thick white border surrounding. To be used in cases where
   * we're showing legacy images that were not uploaded with the intent of being shown in a round container. This effectively
   * shrinks the image, preserving aspect ratio */
  withPadding?: boolean;
  /* Accounts for the sizes at https://www.figma.com/design/3VuLiMz0SPYOQ2373cP6gk/MW-DS?node-id=19-1012&m=dev, but
   * we have historically also used the Avatar with other sizes not specified in the DS. For those cases, leave 'size' as
   * undefined and the Avatar will size to fit its container. */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
} & AvatarIconOrName;

export const AvatarNew = (props: AvatarNewProps) => {
  return (
    <div
      className={cx(
        props.size === 'xs' && 'w-6 h-6 text-xs',
        props.size === 'sm' && 'w-8 h-8 text-sm',
        props.size === 'md' && 'w-10 h-10 text-md',
        props.size === 'lg' && 'w-12 h-12 text-lg',
        props.size === 'xl' && 'w-14 h-14 text-xl',
        props.size === '2xl' && 'w-16 h-16 text-2xl',
        !props.size && 'w-full h-full',
        'border-[0.5px] border-black/8',
        'bg-avatar-bg',
        'flex shrink-0 items-center justify-center relative overflow-clip',
        'rounded-full font-semibold text-avatar-text select-none',
        '@container',
      )}
    >
      {props.url && (
        <div className={cx('flex absolute z-10 w-full h-full', props.withPadding && 'bg-white')}>
          <img
            className={cx(
              props.withPadding ?
                'mx-auto my-auto w-5/7 object-center aspect-auto'
              : 'w-full h-full object-cover',
            )}
            src={props.url}
            alt={props.alt ?? 'Avatar'}
            // Avoid showing the broken image browser icon if the image fails to load
            onError={(event) => {
              event.currentTarget.className = 'hidden';
              // Hide the parent as well, as in the fallback (missing image) case WITH padding the white bkg
              // will still show otherwise
              if (event.currentTarget.parentElement) {
                event.currentTarget.parentElement.className = 'hidden';
              }
            }}
          />
        </div>
      )}
      <span
        className={cx(
          'absolute text-center z-0',
          !props.size &&
            'text-[12px] @min-[30px]:text-[36px] @min-[60px]:text-[40px] @min-[100px]:text-[60px]',
        )}
      >
        {'name' in props && getInitials(props.name)}
        {'fallbackIcon' in props && (
          <>
            {/* Uses container queries https://tailwindcss.com/docs/responsive-design#container-queries with
             * the @container div above to show a different variant of icon based on the size of the @container
             * div
             * This could be simplified if we supported mobile text sizes in <Icon> */}
            <span className='@min-[60px]:hidden'>
              <Icon
                type={props.fallbackIcon}
                size={props.size ? sizeToIconPx[props.size] : '32px'}
              />
            </span>
            <span className='@max-[60px]:hidden @min-[100px]:hidden'>
              <Icon
                type={props.fallbackIcon}
                size={props.size ? sizeToIconPx[props.size] : '50px'}
              />
            </span>
            <span className='@max-[100px]:hidden'>
              <Icon
                type={props.fallbackIcon}
                size={props.size ? sizeToIconPx[props.size] : '80px'}
              />
            </span>
          </>
        )}
      </span>
    </div>
  );
};
